import type { ToolUseItem } from '../../../../contracts/harness.types';
import { isClinicianActionableToolError } from '../../integrations/isClinicianActionableToolError';

export type ToolGroupTone = 'running' | 'success' | 'error' | 'cancelled';

/**
 * One run of same-name calls, as the clinician sees it. The single definition
 * both the grouped row and the Working line read, so the two cannot disagree
 * on what the group holds, how it went, or when it was active.
 */
export interface ToolGroupSummary {
  /** Visible members: a failure the clinician cannot act on renders nothing. */
  members: ToolUseItem[];
  tone: ToolGroupTone;
  total: number;
  done: number;
  failed: number;
  cancelled: number;
  /** The group's own activity: its earliest and latest observed frames. */
  seenAtMs?: { first: number; last: number };
}

const isVisibleMember = (member: ToolUseItem): boolean =>
  member.status !== 'error' || isClinicianActionableToolError(member);

const spanOf = (members: readonly ToolUseItem[]) => {
  let span: { first: number; last: number } | undefined;
  for (const { seenAtMs } of members) {
    if (!seenAtMs) continue;
    span = span
      ? {
          first: Math.min(span.first, seenAtMs.first),
          last: Math.max(span.last, seenAtMs.last),
        }
      : seenAtMs;
  }
  return span;
};

/** `null` when every member is hidden, so the group renders nothing at all. */
export const summariseToolGroup = (
  members: readonly ToolUseItem[]
): ToolGroupSummary | null => {
  const visible = members.filter(isVisibleMember);
  if (visible.length === 0) return null;

  let done = 0;
  let failed = 0;
  let cancelled = 0;
  let running = 0;
  for (const member of visible) {
    if (member.status === 'complete') done += 1;
    else if (member.status === 'error') failed += 1;
    else if (member.status === 'cancelled') cancelled += 1;
    else running += 1;
  }
  const tone: ToolGroupTone =
    running > 0
      ? 'running'
      : failed > 0
        ? 'error'
        : cancelled > 0
          ? 'cancelled'
          : 'success';
  const seenAtMs = spanOf(visible);

  return {
    members: visible,
    tone,
    total: visible.length,
    done,
    failed,
    cancelled,
    ...(seenAtMs ? { seenAtMs } : {}),
  };
};
